// src/forms/data_hub_cors_user_data.js
// Extracted from data_hub.js — handles CORS iframe user data response merging.

import defaultsDeep from 'lodash.defaultsdeep';
import { white_list_outgoing_person_fields, listArrayKeys } from '../helpers/static_vars';
import { validate_and_clean_handlebars_from_user_data } from '../helpers/handlebars_validation';
import { re_prefill_all_forms } from './shared_form_ux_functions';
import { updateActblueConfigWithDatahub } from '../helpers/actblue_integration';

/**
 * Processes CORS iframe user data response.
 * Whitelist-filters, validates handlebars, merges subscription arrays
 * (server is source-of-truth), updates user_data, re-prefills forms,
 * and dispatches the data-hub-new-user-data event.
 *
 * @param {Object} data_hub_instance - The data_hub class instance.
 * @param {Object} payload - Whitelisted user data from the CORS iframe.
 */
export function got_cors_user_data_cache(data_hub_instance, payload) {
    if (!payload) return;

    if (typeof payload !== 'object' || Array.isArray(payload)) {
        return;
    }

    Object.keys(payload).forEach((key) => {
        if (!white_list_outgoing_person_fields.includes(key)) {
            delete payload[key];
        }
    });

    // Defense in depth: don't run the merge / re-prefill / event pipeline when the
    // server (or caller) gave us nothing to merge after whitelist filtering. Still
    // resolve user_data_loaded so callers awaiting init don't hang on an empty
    // response that legitimately has no user_data fields.
    if (Object.keys(payload).length === 0) {
        if (typeof data_hub_instance.resolve_user_data_loaded === 'function') {
            data_hub_instance.resolve_user_data_loaded();
        }
        return;
    }

    // Validate and clean handlebars from CORS payload before merging
    let newMergedUserData;
    try {
        const cleaned_payload = validate_and_clean_handlebars_from_user_data(
            payload,
            white_list_outgoing_person_fields,
            data_hub_instance.error_function
        );

        // ──────────────────────────────────────────────────────────
        // Extract subscription/list arrays from the CORS
        // payload BEFORE the defaultsDeep merge.
        //
        // These arrays are the source-of-truth from the server JWT.
        // They must REPLACE whatever check_or_uncheck_all may have
        // written into user_data during the initial (data-less) render.
        //
        // defaultsDeep only fills missing keys — it won't overwrite an
        // existing subscribedMessageTypeIds array. By pulling them out
        // first, we prevent the broken by-index merge, then assign them
        // back after defaultsDeep finishes.
        // ──────────────────────────────────────────────────────────

        /** @type {Object<string, Array>} */
        const cors_subscription_arrays = {};

        listArrayKeys.forEach((key) => {
            if (Array.isArray(cleaned_payload[key])) {
                cors_subscription_arrays[key] = cleaned_payload[key];
                delete cleaned_payload[key];
            }
        });
        // ──────────────────────────────────────────────────────────

        // Merge the non-subscription fields (existing behavior, unchanged)
        newMergedUserData = defaultsDeep(data_hub_instance.user_data, window?.data_hub_user_data || {}, cleaned_payload);

        // ──────────────────────────────────────────────────────────
        // Overwrite subscription arrays with the
        // authoritative CORS values.
        //
        // If the CORS payload had unsubscribedMessageTypeIds but NOT
        // subscribedMessageTypeIds (common — our user_jwt endpoint
        // only returns unsub arrays when user has no explicit subs),
        // we must also clear subscribedMessageTypeIds so the checkbox
        // logic doesn't see the stale values from check_or_uncheck_all.
        // ──────────────────────────────────────────────────────────
        if (Object.keys(cors_subscription_arrays).length > 0) {
            // Overwrite each array with the CORS version
            Object.keys(cors_subscription_arrays).forEach((key) => {
                newMergedUserData[key] = cors_subscription_arrays[key];
            });

            // Clear the complementary array if CORS didn't provide it.
            // The server is the source of truth — absence means empty.
            if (cors_subscription_arrays.unsubscribedMessageTypeIds &&
                !cors_subscription_arrays.subscribedMessageTypeIds) {
                newMergedUserData.subscribedMessageTypeIds = [];
            }
            if (cors_subscription_arrays.subscribedMessageTypeIds &&
                !cors_subscription_arrays.unsubscribedMessageTypeIds) {
                newMergedUserData.unsubscribedMessageTypeIds = [];
            }
        }
        // ──────────────────────────────────────────────────────────

        data_hub_instance.user_data = newMergedUserData;
        window.data_hub_user_data = newMergedUserData;
    } catch (error) {
        data_hub_instance.error_function?.(`Error validating CORS payload for handlebars: ${error}`, null);
        // If validation fails, merge the original payload
        newMergedUserData = defaultsDeep(data_hub_instance.user_data, window?.data_hub_user_data || {}, payload);
        data_hub_instance.user_data = newMergedUserData;
        window.data_hub_user_data = newMergedUserData;
    }

    // Trigger any necessary callbacks.
    if (typeof data_hub_instance.resolve_user_data_loaded === 'function') {
        data_hub_instance.resolve_user_data_loaded();
    }

    // Notify listeners that new user data is available (e.g. from CORS iframe).
    // Any form that needs to react to late-arriving CORS data can listen for
    // this event — see subscriptions_preferences_form.js for the checkbox example.
    document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
        detail: {
            user_data: data_hub_instance.user_data || {},
            prefill_input_selector: data_hub_instance.prefill_input_selector || null,
            data_hub_instance: data_hub_instance,
            target: data_hub_instance.target || document,
            source: 'cors_iframe'
        },
        bubbles: true,
        composed: true
    }));

    // Re-fill standard form inputs (text, email, phone, etc.)
    re_prefill_all_forms(newMergedUserData, data_hub_instance.prefill_input_selector, data_hub_instance);

    updateActblueConfigWithDatahub(data_hub_instance);
}
